import { Component, Input, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { PublicParkingProcessDto } from '../../../../api/gen';
import { PlateComponent, RegionSeparator, findPlateCountry, normalizePlate } from 'shared-lib';

@Component({
  selector: 'app-current-parking-process',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    DatePipe,
    PlateComponent
  ],
  templateUrl: './current-parking-process.component.html',
  styleUrl: './current-parking-process.component.scss'
})
export class CurrentParkingProcessComponent {
  @Input() process!: PublicParkingProcessDto;

  private readonly router = inject(Router);

  get isActive(): boolean {
    // Same rule as the backend's PublicParkingProcessDto.IsActive, which the public schema does not carry.
    return !!this.process && !this.process.endedAt;
  }

  get statusClass(): string {
    if (this.process?.violationDetails) return 'status-violation';
    if (this.isActive) return 'status-active';
    return 'status-ended';
  }

  get statusText(): string {
    if (!!this.process?.violationDetails) return 'Violation';
    if (this.isActive) return 'In Progress';
    return 'Completed';
  }

  get licensePlateNumber(): string {
    return this.process?.licensePlateDetails?.fullNumber || 'N/A';
  }

  get plateNumber(): string {
    return normalizePlate(this.process?.licensePlateDetails?.fullNumber);
  }

  get plateCountryCode(): string {
    return this.process?.licensePlateDetails?.countryCode?.trim().toUpperCase() || 'AT';
  }

  get plateEuBand(): string {
    return findPlateCountry(this.plateCountryCode)?.euBand ?? this.plateCountryCode;
  }

  get plateRegion(): string {
    return this.process?.licensePlateDetails?.state?.trim().toUpperCase() || '';
  }

  get plateRegionSeparator(): RegionSeparator {
    return findPlateCountry(this.plateCountryCode)?.regionSeparator ?? 'none';
  }

  get zoneName(): string {
    return this.process?.parkingZoneDetails?.name || 'Standard Parking Zone';
  }

  get spaceName(): string {
    return this.process?.parkingZoneDetails?.description || '';
  }

  get startedAt(): string | null {
    return this.process?.startedAt || null;
  }

  get endedAt(): string | null {
    return this.process?.endedAt || null;
  }

  get formattedDuration(): string {
    if (this.startedAt) {
      const start = new Date(this.startedAt).getTime();
      const end = this.endedAt ? new Date(this.endedAt).getTime() : Date.now();
      if (!Number.isNaN(start)) {
        const diffMs = Math.max(0, end - start);
        const totalMinutes = Math.floor(diffMs / 60000);
        const days = Math.floor(totalMinutes / (60 * 24));
        const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
        const minutes = totalMinutes % 60;

        if (days > 0) return `${days}d ${hours}h ${minutes}m`;
        if (hours > 0) return `${hours}h ${minutes}m`;
        return `${minutes}m`;
      }
    }

    if (this.process?.duration) {
      const parts = this.process.duration.split(':');
      if (parts.length >= 2) {
        const hours = parseInt(parts[0], 10);
        const minutes = parseInt(parts[1], 10);
        if (!isNaN(hours) && !isNaN(minutes)) {
          return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
        }
      }
      return this.process.duration;
    }

    return '—';
  }

  get hasViolation(): boolean {
    // Same rule as the backend's PublicParkingProcessDto.HasViolation.
    return !!this.process?.violationDetails;
  }

  get violationType(): string {
    const firstViolation = this.process?.violationDetails?.violationItems?.at(0);
    return firstViolation?.violationType || '';
  }

  goToActivity(): void {
    this.router.navigate(['/activity']);
  }
}
